/**
 * videoSignOn.js — s114 (Dr. Rami 2026-10-22, canvas "Orella Video Visit Sign-On & Consent" V1–V5, approved).
 * PURE, no imports (a gate can run it in node). Before a video visit the patient signs on in the app:
 *   1. identity: the app sign-in, confirmed with Face ID (native, build 80+) or the session PIN when either exists;
 *      neither -> the sign-in alone (D523's base rule: "authenticated by app sign-in", never "skipped");
 *   2. where they are right now: must be one of the physician's license states (the API's licensedStates);
 *   3. the video-visit consent, agreed at EVERY video visit (VIDEO_CONSENT_VERSION names the text).
 * The API (POST /appointments/<id>/video-sign-on) re-checks 2 and 3 and stores the record in the visit's check-in.
 */
export const VIDEO_CONSENT_VERSION = 'tele-consent-1'
export const VIDEO_CONSENT_POINTS = ['consentP1', 'consentP2', 'consentP3', 'consentP4', 'consentP5', 'consentP6']

const NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  PR: 'Puerto Rico',
}

/** 'CA' -> 'California' (unknown codes are returned as given). */
export function stateName(code) {
  const c = String(code || '').toUpperCase()
  return NAMES[c] || c
}

/** The states as one phrase: ['CA'] -> 'California'; ['CA','NV'] -> 'California or Nevada'; 3+ -> 'A, B or C'. */
export function statesPhrase(codes, orWord = 'or') {
  const n = (Array.isArray(codes) ? codes : []).filter(Boolean).map(stateName)
  if (n.length <= 1) return n[0] || ''
  return `${n.slice(0, -1).join(', ')} ${orWord} ${n[n.length - 1]}`
}

/** Which identity confirmation to offer: 'faceid' | 'pin' | 'none'. */
export function identityOffer({ biometricAvailable = false, pinEnabled = false } = {}) {
  if (biometricAvailable) return 'faceid'
  if (pinEnabled) return 'pin'
  return 'none'
}

/** The POST body. `where` is a license-state code the patient chose, or 'other'. null when not ready. */
export function signOnBody({ method, where, agreed }) {
  if (agreed !== true || !where || where === 'other') return null
  return { identityMethod: method === 'faceid' || method === 'pin' ? method : 'app_sign_in',
    locationState: String(where).toUpperCase(), videoConsentVersion: VIDEO_CONSENT_VERSION, consentAgreed: true }
}

/** The sign-on record already stored for THIS appointment in a check-in blob, or null. */
export function storedSignOn(checkIn, appointmentId) {
  const v = checkIn && typeof checkIn === 'object' ? checkIn.videoSignOn : null
  if (!v || typeof v !== 'object') return null
  if (appointmentId && v.appointmentId && String(v.appointmentId) !== String(appointmentId)) return null
  return v
}
